export type ProfileDataState = '已接入' | '部分接入' | '待收资' | '待调研';

export interface ParkProfileFactor {
  label: string;
  description: string;
  state: ProfileDataState;
}

export interface ParkProfile {
  name: string;
  displayName: string;
  subtitle: string;
  levelLabel: string;
  range: {
    imageUrl?: string;
    primaryValue: string;
    primaryLabel: string;
    secondaryValue: string;
    secondaryLabel: string;
    mode: string;
    location: string;
    zones: string[];
    boundaryStatus: string;
  };
  metrics: Array<{ label: string; value: string; note: string }>;
  factors: ParkProfileFactor[];
  energyRows: Array<{ label: string; value: string }>;
  industries: string[];
  keyEnterprises: string;
  reductionDrivers: string;
  transformationDirections: string;
  strengths: string[];
  constraints: string[];
  sources: string[];
}

export const PARK_PROFILE_ORDER = [
  '广州花都经济技术开发区',
  '肇庆高新技术产业开发区',
  '韶关高新技术产业开发区',
  '揭阳高新技术产业开发区',
] as const;

const sharedFactors = (
  energy: ProfileDataState,
  industry: ProfileDataState,
  power: ProfileDataState,
  exportDemand: ProfileDataState,
  readiness: ProfileDataState,
): ParkProfileFactor[] => [
  { label: '能源资源禀赋', description: '绿电规模、开发潜力与综合能源结构', state: energy },
  { label: '产业发展基础', description: '主导产业、重点企业与产业链完整度', state: industry },
  { label: '电力安全可靠供应', description: '变电站、网架、负荷预测与调节能力', state: power },
  { label: '外向型企业减碳需求', description: '出口占比、碳关税与绿色供应链要求', state: exportDemand },
  { label: '建设意愿与基础条件', description: '申报意愿、工作机制、平台与改造基础', state: readiness },
];

export const PARK_PROFILES: Record<string, ParkProfile> = {
  广州花都经济技术开发区: {
    name: '广州花都经济技术开发区',
    displayName: '广州花都经济技术开发区',
    subtitle: '广州市 · 花都区',
    levelLabel: '国家级经济技术开发区',
    range: {
      imageUrl: '/images/park-profiles/huadu-zero-carbon-park-range.png',
      primaryValue: '14.99 km²',
      primaryLabel: '国家级经开区核准范围',
      secondaryValue: '279.88 km²',
      secondaryLabel: '经开区实际统筹产业空间',
      mode: '一区多园',
      location: '花都区产业核心区',
      zones: ['临空数智港东翼', '汽车城北片区', '西部智能新能源汽车城'],
      boundaryStatus: '当前展示规划范围示意，正式四至边界图层待接入',
    },
    metrics: [
      { label: '综合能源消费量', value: '20万~100万吨', note: '标准煤 · 当前数据档位' },
      { label: '绿电资源规模', value: '600 MW 以下', note: '园区资源档位' },
      { label: '园区地区生产总值', value: '1,038 亿元', note: '2022年 · 官方园区口径' },
      { label: '分布式光伏', value: '275 MW', note: '已并网投运；备案1544MW' },
    ],
    factors: sharedFactors('部分接入', '已接入', '待收资', '待调研', '已接入'),
    energyRows: [
      { label: '绿电资源', value: '分布式光伏逐步推广，可依托大湾区电网外购绿电' },
      { label: '清洁能源项目', value: '新型储能、光伏组件、退役动力电池及光伏板回收利用项目已布局' },
      { label: '供电可靠性', value: '主变容量、线路结构、N-1能力与增量负荷待专项收资' },
      { label: '灵活调节', value: '虚拟电厂、需求响应、综合能源站与集中供冷需求待确认' },
    ],
    industries: ['新能源与绿色装备制造', '汽车及智能网联新能源汽车', '临空高科技', '新材料与智能制造', '生物医药与健康产业'],
    keyEnterprises: '东风日产及汽车产业链、广州高景太阳能、赛拉弗、中恒源等',
    reductionDrivers: '出口产品碳足迹、绿色供应链要求、汽车产业转型和园区招商竞争力',
    transformationDirections: '绿电替代、屋顶光伏、储能、动力电池循环利用、能碳管理与综合能源服务',
    strengths: ['国家级经开区政策基础', '新能源汽车产业链基础较强', '新能源项目集中布局', '空铁联运枢纽区位'],
    constraints: ['园区整体能耗及碳排放分项数据待补齐', '自主绿电资源有限', '三个核准区块空间分散', '高耗能传统产业仍有存量'],
    sources: ['广州花都发改局对接事宜情况（2026-09-07）', '广州花都经济技术开发区基础资料', '政府公开资料及园区官方来源'],
  },
  肇庆高新技术产业开发区: {
    name: '肇庆高新技术产业开发区',
    displayName: '肇庆高新技术产业开发区',
    subtitle: '肇庆市 · 四会市大旺',
    levelLabel: '国家级高新技术产业开发区',
    range: {
      imageUrl: '/images/park-profiles/zhaoqing-hightech-park-range.png',
      primaryValue: '13.10 km²',
      primaryLabel: '肇庆高新区起步区',
      secondaryValue: '大型产业集聚区',
      secondaryLabel: '广东省（肇庆）多片区布局',
      mode: '一区多园',
      location: '四会大旺及周边协同片区',
      zones: ['肇庆高新区起步区', '四会市起步区', '四会大沙市管起步区', '鼎湖—肇庆新区起步区'],
      boundaryStatus: '当前展示广东省（肇庆）大型产业集聚区布局示意，正式四至边界图层待接入',
    },
    metrics: [
      { label: '综合能源消费量', value: '20万~100万吨', note: '标准煤 · 当前数据档位' },
      { label: '绿电资源规模', value: '600 MW 以下', note: '园区资源档位' },
      { label: '园区地区生产总值', value: '276.52 亿元', note: '2025年 · 官方园区口径' },
      { label: '投运光伏', value: '503 MW', note: '现有资料口径；用户侧储能51 MWh' },
    ],
    factors: sharedFactors('已接入', '已接入', '部分接入', '待调研', '已接入'),
    energyRows: [
      { label: '绿电资源', value: '投运光伏持续增长，可通过电网外购补充绿电' },
      { label: '能源平台', value: '源网荷储一体化调控平台已接入231家企业和58个分布式光伏站点' },
      { label: '供电与调节', value: '用户侧储能51MWh，国能二期2×400MW天然气发电项目建设中' },
      { label: '综合利用', value: '山鹰纸业沼气发电和余压发电项目已投运' },
    ],
    industries: ['新能源汽车及零部件', '新型储能', '新能源与绿色装备制造', '高端制造与先进装备', '生物医药与健康产业'],
    keyEnterprises: '小鹏汽车、瑞庆时代（宁德时代全资子公司）及上下游产业链企业',
    reductionDrivers: '全国首批碳达峰试点、动力电池绿色供应链、园区申报及产业链低碳要求',
    transformationDirections: '绿电直连、外购绿电、光储充一体化、企业节能改造和能源数字化管控',
    strengths: ['全国首批碳达峰试点园区', '源网荷储平台基础成熟', '新能源汽车与储能链主集聚', '光伏和用户侧储能已有规模'],
    constraints: ['绿电占比仍需提升', '外部绿电依赖较高', '园区边界计量与碳核算复杂', '中小企业改造投入能力不均'],
    sources: ['肇庆市高新技术产业区对接事宜（2026-08-17）', '肇庆高新技术产业开发区基础资料', '广东省（肇庆）大型产业集聚区布局图', '中国高新技术产业导报／南方+（2025年GDP）'],
  },
  韶关高新技术产业开发区: {
    name: '韶关高新技术产业开发区',
    displayName: '韶关高新技术产业开发区',
    subtitle: '韶关市 · 武江区、浈江区',
    levelLabel: '高新技术产业开发区',
    range: {
      imageUrl: '/images/park-profiles/shaoguan-hightech-park-range.png',
      primaryValue: '核心区 + 十大园区',
      primaryLabel: '市域产业空间体系',
      secondaryValue: '一区多园',
      secondaryLabel: '高新区核心区协同十大园区',
      mode: '多片区协同',
      location: '粤北产业转移核心平台',
      zones: ['高新区核心区', '乳源高新区', '临港园', '东部科教园'],
      boundaryStatus: '当前展示市域产业空间规划示意，正式四至边界图层待接入',
    },
    metrics: [
      { label: '综合能源消费量', value: '20万~100万吨', note: '标准煤 · 当前数据档位' },
      { label: '绿电资源规模', value: '600 MW 以下', note: '园区资源档位' },
      { label: '工业总产值', value: '851.3 亿元', note: '2023年 · 一区多园口径；未公布地区生产总值' },
      { label: '分布式光伏', value: '2.02 MW', note: '百通泰立项目已并网运营' },
    ],
    factors: sharedFactors('部分接入', '已接入', '待收资', '待调研', '部分接入'),
    energyRows: [
      { label: '绿电资源', value: '粤北风能、太阳能资源较丰富，开发利用程度仍需提升' },
      { label: '建设模式', value: '相关项目提出源网荷储云一体化、绿电直连、储能和综合能源管理平台' },
      { label: '供电可靠性', value: '110千伏变电站、线路和算力增量负荷资料待收资' },
      { label: '算力负荷', value: '大数据产业算力规模居全省前列，绿电替代和可靠供电需求较高' },
    ],
    industries: ['高端制造与先进装备', '数字经济与算力 / 数据中心', '生物医药与健康产业', '绿色产业'],
    keyEnterprises: '重点企业、出口企业和用能企业名录待专项调研',
    reductionDrivers: '算力负荷绿色用能、大湾区产业转移承接和绿色制造体系建设',
    transformationDirections: '绿电直连、储能、算力负荷协同、清洁生产和园区能源管理平台',
    strengths: ['粤北可再生能源资源条件', '算力产业基础', '一区三园绿色产业体系', '已有零碳园区项目启动'],
    constraints: ['正式规划范围和四至待核实', '能耗碳排核心指标未公开', '绿电项目仍处起步阶段', '算力产业绿电替代压力较大'],
    sources: ['韶关高新技术产业开发区基础资料', '韶关高新区简介（管委会，2023年工业总产值）'],
  },
  揭阳高新技术产业开发区: {
    name: '揭阳高新技术产业开发区',
    displayName: '揭阳高新技术产业开发区',
    subtitle: '揭阳市 · 榕城区',
    levelLabel: '高新技术产业开发区',
    range: {
      imageUrl: '/images/park-profiles/jieyang-hightech-park-range.png',
      primaryValue: '50.03 km²',
      primaryLabel: '直管核心区总面积（2022年扩区后）',
      secondaryValue: '6.98 km²',
      secondaryLabel: '扩区前原有面积',
      mode: '三片区布局',
      location: '汕潮揭都市圈地理中心',
      zones: ['榕江新城片区（12.86 km²）', '临空片区（15.63 km²）', '临港片区（21.54 km²）'],
      boundaryStatus: '当前展示直管核心区规划范围示意，正式四至边界图层待接入',
    },
    metrics: [
      { label: '综合能源消费量', value: '20万~100万吨', note: '标准煤 · 当前数据档位' },
      { label: '绿电资源规模', value: '600 MW 以下', note: '园区资源档位' },
      { label: '园区地区生产总值', value: '103.2 亿元', note: '2025年 · 官方园区口径' },
      { label: '属地新能源装机', value: '1.65 GW', note: '风电及集中式光伏合计' },
    ],
    factors: sharedFactors('已接入', '已接入', '部分接入', '待调研', '部分接入'),
    energyRows: [
      { label: '绿电资源', value: '属地海上风电、陆上风电和集中式光伏资源体量较大' },
      { label: '本地消纳', value: '存量新能源本地消纳率不足35%，绿电直供专线尚未建成' },
      { label: '供电与调节', value: '源网荷储一体化仍处前期规划阶段' },
      { label: '资源利用', value: '工业固废综合利用率61%，余热余压利用率28%' },
    ],
    industries: ['高端制造与先进装备', '通用 / 专用设备制造', '智能装备制造', '新材料', '现代服务业'],
    keyEnterprises: '重点企业名录、出口占比及企业减碳需求待专项调研',
    reductionDrivers: '高耗能环节改造、海上风电产业链、产品碳足迹和外向型企业绿色供应链',
    transformationDirections: '绿电直供、源网荷储、企业能效提升、余热余压利用和工业固废循环利用',
    strengths: ['属地新能源资源体量较大', '智能装备与新材料产业基础', '具备国家级零碳园区申报方向', '临空与港口区位条件'],
    constraints: ['高耗能产业占比较高', '本地新能源消纳能力偏弱', '绿电直供体系未建成', '园区正式边界和电网资料待补齐'],
    sources: ['揭阳高新技术产业开发区基础资料'],
  },
};

export function hasParkProfile(name: string): boolean {
  return Boolean(PARK_PROFILES[name]);
}
